import { useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CATEGORY_MAP, PRICING } from "@/data/categories";
import { api, formatApiErrorDetail } from "@/lib/api";
import { downloadPdf } from "@/lib/download";
import { MemoryBody } from "@/components/memory/MemoryBody";
import { MemoryEditForm, EDITABLE_KEYS } from "@/components/memory/MemoryEditForm";
import { MemoryActions } from "@/components/memory/MemoryActions";
import { MemoryInsightPanel } from "@/components/memory/MemoryInsightPanel";
import { ConfirmDeleteDialog } from "@/components/memory/ConfirmDeleteDialog";
import { CaretLeft, PencilSimple } from "@phosphor-icons/react";

const pick = (ev) => Object.fromEntries(EDITABLE_KEYS.map((k) => [k, ev[k] || ""]));

const niceDate = (d) => {
  if (!d) return "";
  try { return format(new Date(d), "d MMMM yyyy"); } catch { return d; }
};

export function EventDetailDialog({ event, open, onOpenChange, onChanged, insightAccess, focusInsight }) {
  const [current, setCurrent] = useState(event);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [showInsight, setShowInsight] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const insightRef = useRef(null);

  useEffect(() => {
    setCurrent(event);
    setEditing(false);
    setShowInsight(!!focusInsight || !!event?.memory_insight?.text);
  }, [event, focusInsight]);

  useEffect(() => {
    if (showInsight && open) setTimeout(() => insightRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 150);
  }, [showInsight, open]);

  if (!current) return null;
  const isDream = current.entry_type === "dream";
  const cat = CATEGORY_MAP[current.category] || { color: "#9C8B76", label: current.custom_event || (isDream ? "Dream" : "Moment") };

  const access = insightAccess?.(current);
  const insightPrice = PRICING.ai.find((a) => a.key === "memory_insight")?.price;
  const insightLabel = current.memory_insight?.text || access === "paid" || access === "admin"
    ? "Memory Insight"
    : access === "gift" ? "Memory Insight · Free gift" : `Memory Insight · ${PRICING.currency}${insightPrice?.toFixed(2)}`;

  const save = async () => {
    if (!draft.title?.trim()) { toast.error("Please give this memory a title."); return; }
    setSaving(true);
    try {
      const r = await api.put(`/events/${current.id}`, draft);
      setCurrent(r.data);
      setEditing(false);
      toast.success("Changes saved");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save your changes.");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (focusDate) => {
    setDraft(pick(current));
    setEditing(true);
    if (focusDate) setTimeout(() => document.querySelector('[data-testid="edit-event_date"]')?.focus(), 80);
  };

  const remove = async () => {
    try {
      await api.delete(`/events/${current.id}`);
      toast.success("Memory deleted");
      onChanged?.();
      onOpenChange(false);
    } catch {
      toast.error("Could not delete.");
    }
  };

  const print = () => downloadPdf(`/events/${current.id}/pdf`, current.title).catch(() => toast.error("Could not prepare the PDF."));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="event-detail-dialog" className="max-h-[92vh] overflow-y-auto border-[#4A4540]/15 bg-[#FDFBF7] sm:max-w-xl">
        <DialogTitle className="sr-only">{current.title}</DialogTitle>
        <DialogDescription className="sr-only">The full memory you saved</DialogDescription>
        <button data-testid="memory-back-btn" onClick={() => onOpenChange(false)}
          className="-ml-1 flex items-center gap-1 self-start rounded-full px-2 py-1 text-sm font-semibold text-[#2C2A28] hover:bg-[#F4EFE6]">
          <CaretLeft size={18} weight="bold" /> Back to list
        </button>
        <div className="flex flex-wrap items-center gap-2 pr-8">
          <span className="rounded-full px-3 py-0.5 text-xs font-medium text-white" style={{ backgroundColor: isDream ? "#C99A5B" : cat.color }}>
            {isDream ? "Dream" : cat.label}
          </span>
          {current.season && !isDream && <span className="rounded-full bg-[#F4EFE6] px-3 py-0.5 text-xs text-[#8A7F76]">{current.season}</span>}
        </div>

        {editing ? (
          <MemoryEditForm draft={draft} onChange={setDraft} />
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3">
              {current.event_date && <p data-testid="memory-full-date" className="font-['Caveat',cursive] text-3xl text-[#A84A32]">{niceDate(current.event_date)}</p>}
              <button data-testid="memory-edit-date-btn" onClick={() => startEdit(true)}
                className="flex items-center gap-1 rounded-full border border-[#2C2A28]/15 px-3 py-1 text-xs font-semibold text-[#4A4540] hover:bg-[#F4EFE6]">
                <PencilSimple size={13} /> {current.event_date ? "Edit date" : "Add a date"}
              </button>
            </div>
            <h2 data-testid="memory-full-title" className="font-serif text-3xl leading-tight text-[#2C2A28]">{current.title}</h2>
            <MemoryBody event={current} />
          </>
        )}

        {showInsight && !isDream && !editing && (
          <div ref={insightRef}>
            <MemoryInsightPanel event={current} access={access}
              onSaved={(mi) => { setCurrent((c) => ({ ...c, memory_insight: mi })); onChanged?.(); }} />
          </div>
        )}

        <MemoryActions editing={editing} saving={saving}
          onEdit={() => startEdit(false)}
          onSave={save} onDelete={() => setConfirmDelete(true)} onPrint={print} insightLabel={insightLabel}
          onInsight={() => { if (isDream) { toast("Memory Insights are for memories, not dreams."); return; } setEditing(false); setShowInsight(true); insightRef.current?.scrollIntoView({ behavior: "smooth" }); }} />
        {editing && (
          <button data-testid="memory-cancel-edit" onClick={() => setEditing(false)} className="-mt-2 self-start text-sm text-[#8A7F76] hover:text-[#A84A32]">
            Cancel editing
          </button>
        )}
      </DialogContent>
      <ConfirmDeleteDialog open={confirmDelete} onOpenChange={setConfirmDelete} title={current.title} onConfirm={remove} />
    </Dialog>
  );
}
